/**
 * 21/10/2026 — the route test editor's live result (user: "how can I see
 * the best in order … in the automation gateway strategy"): today's test
 * drops ranked by the formula and rules being edited — the same ranking the
 * engine reads for the winner, so a change shows at once how the order
 * would move. A route test not saved yet has no drops to rank.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, ReactNode } from 'react';
import { ListOrdered } from 'lucide-react';
import type { DropGenGatewayRanking } from '@trafficpulse/shared';

import { Button, HintDot, Skeleton } from '@/components/ui';
import { cn } from '@/lib/utils';

import { RankingTable } from '../../../shared/route-test/RankingTable';
import { progressWords, rankingHeadline } from '../../../shared/route-test/ranking-words';
import { useRankingPreview } from './use-ranking-preview';

const HINT =
  "Live and read-only: today's test drops of this route test, scored by the formula and rules as you edit them — the same ranking the engine reads once every test drop has settled. Saved formulas and rules are what the engine uses.";

const Panel: FC<{ children: ReactNode; busy?: boolean }> = ({ children, busy = false }) => (
  <div className={cn('flex flex-col gap-2 rounded-lg border border-border/70 bg-muted/10 p-3 transition-opacity', busy && 'opacity-70')} data-testid="route-test-result">
    <p className="flex items-center gap-1.5 text-xs font-semibold">
      <ListOrdered className="h-3.5 w-3.5 text-primary" aria-hidden />
      Today&apos;s test drops, ranked by this formula
      <HintDot label={HINT} />
    </p>
    {children}
  </div>
);

const Live: FC<{ countryKey: string; containerId: string; ranking: DropGenGatewayRanking }> = ({ countryKey, containerId, ranking }) => {
  const q = useRankingPreview(countryKey, containerId, ranking);
  if (q.isLoading) return <Panel><Skeleton className="h-24 w-full" /></Panel>;
  if (q.isError || !q.data)
    return (
      <Panel>
        <p className="text-xs text-muted-foreground">The ranking could not be read right now.</p>
        <Button size="sm" variant="outline" className="h-7 w-fit text-xs" onClick={() => void q.refetch()}>Retry</Button>
      </Panel>
    );
  const d = q.data;
  return (
    <Panel busy={q.pending || q.isFetching}>
      <p className="text-[11px] text-muted-foreground" data-testid="route-test-result-line">
        {rankingHeadline(d, { at: null, progress: progressWords(d.drops.sent, d.drops.planned, 'sent') })}
      </p>
      {d.rows.length > 0 ? <RankingTable rows={d.rows} /> : <p className="text-xs text-muted-foreground">{d.note}</p>}
    </Panel>
  );
};

export const RankingResult: FC<{ countryKey: string; containerId: string | null; ranking: DropGenGatewayRanking }> = ({ countryKey, containerId, ranking }) =>
  containerId ? (
    <Live countryKey={countryKey} containerId={containerId} ranking={ranking} />
  ) : (
    <Panel>
      <p className="text-xs text-muted-foreground">Save the route test to see today&apos;s ranking.</p>
    </Panel>
  );
